import { Platform } from "react-native";

/**
 * True when the app is running in a mobile browser (Expo web on phone/tablet).
 * Used to enable camera barcode scanning only on mobile web, not desktop.
 */
export function isMobileWeb() {
  if (Platform.OS !== "web" || typeof navigator === "undefined") {
    return false;
  }
  const ua = navigator.userAgent || "";
  const mobileUa = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const coarsePointer =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(pointer: coarse)").matches;
  const narrowViewport = typeof window !== "undefined" && window.innerWidth <= 900;
  return mobileUa || coarsePointer || narrowViewport;
}

/**
 * iOS Safari (iOS 17+) shows a Passwords/Cards/Contacts AutoFill bar above the
 * keyboard for any plain text input and ignores autocomplete="off". The only
 * reliable suppression is to render the element as <input type="search">,
 * since Safari does not run AutoFill for search inputs.
 *
 * Apply this via a `ref` callback on a TextInput so it converts the underlying
 * DOM <input> to type="search" on web (no-op on native, no-op on <textarea>
 * which RN Web renders for multiline). Also tags it with a CSS class so the
 * native search-cancel "x" button is hidden in styles.css.
 *
 * Pass the component's existing ref (object or callback) as `existingRef` so it
 * still receives the node:
 *
 *   <TextInput ref={noAutofillRef(this.inputRef)} ... />
 */
export function noAutofillRef(existingRef) {
  return (el) => {
    if (typeof existingRef === "function") {
      existingRef(el);
    } else if (existingRef && typeof existingRef === "object") {
      try {
        existingRef.current = el;
      } catch (e) {}
    }
    if (Platform.OS !== "web" || !el) return;
    if (!isMobileWeb()) return;
    try {
      const tag = (el.tagName || "").toUpperCase();
      if (tag === "INPUT" && el.type !== "search") {
        el.type = "search";
      }
      const cls = el.getAttribute("class") || "";
      if (!cls.includes("no-autofill-input")) {
        el.setAttribute("class", (cls + " no-autofill-input").trim());
      }
      el.setAttribute("autocomplete", "off");
      el.setAttribute("autocorrect", "off");
      el.setAttribute("autocapitalize", "off");
      el.setAttribute("spellcheck", "false");
      el.setAttribute("name", "search");
      el.setAttribute("aria-autocomplete", "list");
    } catch (e) {}
  };
}

/**
 * Plain props version, kept for inputs that don't expose a settable ref easily.
 * Less effective than `noAutofillRef` because Safari often ignores these on
 * non-search input types, but harmless on native (returns {}).
 */
export function getNoAutofillProps() {
  if (Platform.OS !== "web") return {};
  return {
    autoComplete: "off",
    autoCorrect: false,
    spellCheck: false,
  };
}
